// client/src/components/analytics/LinkManager.jsx
//
// /:tenant/links — every tracking link the tenant has (auto-wrapped post and
// email links, QR links, manual links) with clicks, unique visitors,
// conversions and revenue. Create a manual link, copy it, download its QR
// code, switch a link off, and turn link auto-wrapping on or off.
// Growth Phase 1, feature_key "analytics_attribution".
//
// API: /api/analytics/:tenant/links (GET, POST), /links/:id (PATCH),
//      /links/:id/qr, PATCH /api/tenants/:tenant/settings/auto-wrap-links

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaHome } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import { getJson, patchJson, postJson } from '../../services/http';
import { formatCount, formatMoney } from './attributionFormat';
import './PerformancePanel.css';

const BASE_URL = process.env.REACT_APP_BASE_URL?.replace(/\/$/, '') || '';
const SOURCE_FILTERS = ['', 'social_post', 'email', 'qr', 'manual'];

export default function LinkManager({ tenantId: tenantProp }) {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const tenant = Number(tenantProp || params.tenant);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [source, setSource] = useState('');
  const [form, setForm] = useState({ destination: '', label: '' });
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null); // { kind: 'ok'|'error', text }

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams({ days: '90' });
      if (source) qs.set('sourceType', source);
      const res = await getJson(`/api/analytics/${tenant}/links?${qs}`);
      setData(res.data || null);
    } catch {
      setError(t('links.loadError', 'Could not load links.'));
    } finally {
      setLoading(false);
    }
  }, [tenant, source, t]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setNotice(null);
    let dest = form.destination.trim();
    if (dest && !/^https?:\/\//i.test(dest)) dest = `https://${dest}`;
    setSaving(true);
    try {
      const res = await postJson(`/api/analytics/${tenant}/links`, { destination: dest, label: form.label.trim() || undefined });
      setForm({ destination: '', label: '' });
      setNotice({ kind: 'ok', text: t('links.created', 'Link created: {{url}}', { url: res.data?.trackingUrl }) });
      load();
    } catch (err) {
      setNotice({ kind: 'error', text: err?.response?.data?.error || t('links.createError', 'Could not create the link.') });
    } finally {
      setSaving(false);
    }
  };

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setNotice({ kind: 'ok', text: t('links.copied', 'Copied {{url}}', { url }) });
    } catch {
      setNotice({ kind: 'error', text: t('links.copyError', 'Copy failed. Select the link and copy it manually.') });
    }
  };

  const setActive = async (link, isActive) => {
    try {
      await patchJson(`/api/analytics/${tenant}/links/${link.linkId}`, { isActive });
      load();
    } catch {
      setNotice({ kind: 'error', text: t('links.updateError', 'Could not update the link.') });
    }
  };

  const setAutoWrap = async (enabled) => {
    try {
      await patchJson(`/api/tenants/${tenant}/settings/auto-wrap-links`, { enabled });
      setData((d) => (d ? { ...d, autoWrapLinks: enabled } : d));
    } catch {
      setNotice({ kind: 'error', text: t('links.settingError', 'Could not save the setting.') });
    }
  };

  const qrHref = (link, format) => `${BASE_URL}/api/analytics/${tenant}/links/${link.linkId}/qr?format=${format}&download=1`;
  const sourceLabel = (s) => t(`links.sources.${s || 'all'}`, s || 'All');

  const links = data?.links || [];

  return (
    <div className="lm-page" style={{ paddingBottom: 72 }}>
      <section className="surface">
        <h4>{t('links.title', 'Tracked links')}</h4>
        <p className="surface-muted perf-small">
          {t('links.intro', 'Every On-Screen link in a scheduled post or campaign email becomes a tracked link, one per post and platform. Use a manual link for flyers, bios and anywhere else.')}
        </p>
        {data && (
          <label className="lm-toggle" style={{ marginTop: 8 }}>
            <input
              type="checkbox"
              checked={!!data.autoWrapLinks}
              onChange={(e) => setAutoWrap(e.target.checked)}
            />
            {t('links.autoWrap', 'Track On-Screen links in posts and email automatically')}
          </label>
        )}
      </section>

      <section className="surface">
        <h4>{t('links.newLink', 'New link')}</h4>
        <form className="lm-form" onSubmit={create}>
          <label>
            {t('links.destination', 'Destination URL')}
            <input
              type="text"
              inputMode="url"
              required
              placeholder="https://on-screen.app/ev/…"
              value={form.destination}
              onChange={(e) => setForm((f) => ({ ...f, destination: e.target.value }))}
            />
          </label>
          <label>
            {t('links.label', 'Name (optional)')}
            <input
              type="text"
              maxLength={160}
              placeholder={t('links.labelPlaceholder', 'Spring flyer')}
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            />
          </label>
          <button type="submit" className="lm-btn" disabled={saving}>
            {saving ? t('common.loading', 'Loading...') : t('links.create', 'Create link')}
          </button>
        </form>
      </section>

      {notice && <div className={`note ${notice.kind === 'ok' ? 'ok' : 'error'}`} role="status">{notice.text}</div>}

      <section className="surface">
        <div className="perf-head">
          <h4>{t('links.allLinks', 'Links')}</h4>
          <select className="lm-filter" value={source} onChange={(e) => setSource(e.target.value)} aria-label={t('links.filter', 'Filter by source')}>
            {SOURCE_FILTERS.map((s) => <option key={s || 'all'} value={s}>{sourceLabel(s)}</option>)}
          </select>
        </div>

        {loading && !data ? (
          <LoadingState inline />
        ) : error ? (
          <ErrorState message={error} onRetry={load} />
        ) : links.length === 0 ? (
          <div className="note info">{t('links.empty', 'No tracked links yet.')}</div>
        ) : (
          <div className="perf-table-wrap">
            <table className="perf-table">
              <thead>
                <tr>
                  <th>{t('links.link', 'Link')}</th>
                  <th className="num">{t('performance.clicks', 'Clicks')}</th>
                  <th className="num">{t('performance.uniqueVisitors', 'Unique visitors')}</th>
                  <th className="num">{t('performance.conversions', 'Conversions')}</th>
                  <th className="num">{t('performance.revenue', 'Revenue')}</th>
                  <th>{t('links.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {links.map((l) => (
                  <tr key={l.linkId}>
                    <td>
                      <div>
                        <span className="lm-badge">{sourceLabel(l.sourceType)}</span>
                        {l.platform && <span className="lm-badge">{t(`performance.platforms.${l.platform}`, l.platform)}</span>}
                        {!l.isActive && <span className="lm-badge off">{t('links.off', 'Off')}</span>}
                        {l.label && <strong>{l.label}</strong>}
                      </div>
                      <div className="lm-url">{l.trackingUrl}</div>
                      <div className="lm-dest">→ {l.destination}</div>
                    </td>
                    <td className="num">{formatCount(l.clicks)}</td>
                    <td className="num">{formatCount(l.uniqueVisitors)}</td>
                    <td className="num">{formatCount(l.conversions)}</td>
                    <td className="num">{formatMoney(l.revenue)}</td>
                    <td>
                      <div className="lm-actions">
                        <button type="button" className="lm-btn secondary" onClick={() => copy(l.trackingUrl)}>
                          {t('links.copy', 'Copy')}
                        </button>
                        <a className="lm-btn secondary" href={qrHref(l, 'png')} download>
                          {t('links.qrPng', 'QR (PNG)')}
                        </a>
                        <a className="lm-btn secondary" href={qrHref(l, 'svg')} download>
                          {t('links.qrSvg', 'QR (SVG)')}
                        </a>
                        <button type="button" className="lm-btn secondary" onClick={() => setActive(l, !l.isActive)}>
                          {l.isActive ? t('links.turnOff', 'Turn off') : t('links.turnOn', 'Turn on')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && !data.attributionReady && (
          <div className="note warn" style={{ marginTop: 10 }}>
            {t('performance.attributionPending', 'Clicks show here now. Crediting conversions to posts starts after a database update.')}
          </div>
        )}
      </section>

      <AppFooter
        buttons={[
          { icon: <FaArrowLeft />, label: t('common.back', 'Back'), onClick: () => navigate(-1) },
          { icon: <FaHome />, label: t('common.home', 'Home'), onClick: () => navigate(`/${tenant}/dashboard`) },
        ]}
      />
    </div>
  );
}
